import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight, LuCornerDownLeft, LuLoaderCircle } from 'react-icons/lu';
import { useTranslation } from '../../i18n/LanguageContext';

const NAV_BUTTON = [
  'tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-500 tw-transition-colors',
  'hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-35 disabled:hover:tw-bg-transparent',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
  'dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white',
].join(' ');

// Up to five page numbers around the current page
const getPageNumbers = (currentPage, totalPages) => Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
  if (totalPages <= 5 || currentPage <= 3) return i + 1;
  if (currentPage >= totalPages - 2) return totalPages - 4 + i;
  return currentPage - 2 + i;
});

/**
 * Which rows are on screen ("1–9 / 101 …") and page navigation under a data
 * table. `rangeOf` is the page's own "/ 101 müşteri" text; `ofTotal` is shown
 * while a search or filter narrows the list. `compact` (narrow screens) shows
 * "2 / 11" between the arrows instead of page numbers. `jump` ({ label,
 * onJump(text) }) adds a "go to page" field for long lists; the page checks
 * the number.
 */
const TablePagination = ({ rangeStart, rangeEnd, resultCount, rangeOf, ofTotal, currentPage, totalPages, isLoading, compact, onPage, jump }) => {
  const { t } = useTranslation();
  const [jumpValue, setJumpValue] = useState('');
  const atStart = currentPage <= 1;
  const atEnd = currentPage >= totalPages;

  return (
    <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-gap-2 tw-border-t tw-border-slate-200/70 tw-px-4 tw-py-2.5 sm:tw-flex-row sm:tw-justify-between dark:tw-border-white/10">
      <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400" aria-live="polite">
        {resultCount === 0 ? (
          <span className="tw-inline">{t('dataTable.pagination.noResults')}</span>
        ) : (
          <span className="tw-inline">
            <strong className="tw-font-semibold tw-text-slate-900 tw-tabular-nums dark:tw-text-white">{rangeStart}–{rangeEnd}</strong>
            {' '}
            {rangeOf}
            {ofTotal && <span className="tw-hidden sm:tw-inline"> · {ofTotal}</span>}
          </span>
        )}
        {isLoading && (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-teal-600 dark:tw-text-teal-300">
            <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
            {t('dataTable.pagination.loading')}
          </span>
        )}
      </p>

      {/* Always rendered: a footer that changed height would change how many rows fit */}
      <nav aria-label={t('dataTable.pagination.pages')} className="tw-flex tw-items-center tw-gap-0.5">
        <button type="button" onClick={() => onPage(1)} disabled={atStart} aria-label={t('dataTable.pagination.first')} title={t('dataTable.pagination.first')} className={NAV_BUTTON}>
          <LuChevronsLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        <button type="button" onClick={() => onPage(currentPage - 1)} disabled={atStart} aria-label={t('dataTable.pagination.previous')} title={t('dataTable.pagination.previous')} className={NAV_BUTTON}>
          <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        {compact && (
          <span className="tw-min-w-[4.5rem] tw-px-2 tw-text-center tw-text-sm tw-font-semibold tw-text-slate-700 tw-tabular-nums dark:tw-text-slate-200">
            {currentPage} / {totalPages}
          </span>
        )}
        {!compact && getPageNumbers(currentPage, totalPages).map((page) => {
          const active = page === currentPage;
          return (
            <button
              key={page}
              type="button"
              onClick={() => onPage(page)}
              aria-current={active ? 'page' : undefined}
              aria-label={t('dataTable.pagination.page', { page })}
              className={`tw-h-9 tw-min-w-[2.25rem] tw-rounded-full tw-px-2 tw-text-sm tw-font-semibold tw-tabular-nums tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                active
                  ? 'tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-md tw-shadow-teal-500/30'
                  : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white'
              }`}
            >
              {page}
            </button>
          );
        })}
        <button type="button" onClick={() => onPage(currentPage + 1)} disabled={atEnd} aria-label={t('dataTable.pagination.next')} title={t('dataTable.pagination.next')} className={NAV_BUTTON}>
          <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        <button type="button" onClick={() => onPage(totalPages)} disabled={atEnd} aria-label={t('dataTable.pagination.last')} title={t('dataTable.pagination.last')} className={NAV_BUTTON}>
          <LuChevronsRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        {jump && !compact && totalPages > 1 && (
          <form
            className="tw-ml-2 tw-flex tw-items-center tw-gap-1 tw-border-l tw-border-slate-200/80 tw-pl-3 dark:tw-border-white/10"
            onSubmit={(event) => {
              event.preventDefault();
              if (jump.onJump(jumpValue) !== false) setJumpValue('');
            }}
          >
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              placeholder={String(currentPage)}
              onChange={(event) => setJumpValue(event.target.value)}
              aria-label={jump.label}
              title={jump.label}
              className="tw-h-8 tw-w-16 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-px-2.5 tw-text-center tw-text-sm tw-tabular-nums tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500/40 dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-200"
            />
            <button type="submit" disabled={isLoading} aria-label={jump.label} title={jump.label} className={NAV_BUTTON}>
              <LuCornerDownLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
            </button>
          </form>
        )}
      </nav>
    </div>
  );
};

TablePagination.propTypes = {
  rangeStart: PropTypes.number.isRequired,
  rangeEnd: PropTypes.number.isRequired,
  resultCount: PropTypes.number.isRequired,
  rangeOf: PropTypes.string.isRequired,
  ofTotal: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  compact: PropTypes.bool,
  onPage: PropTypes.func.isRequired,
  jump: PropTypes.shape({ label: PropTypes.string.isRequired, onJump: PropTypes.func.isRequired }),
};

export default TablePagination;
